'use client';

import Link from 'next/link';
import { ArrowRight, Building2, Landmark, HeartHandshake, Trees, Library, School } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';

const TRUST_ICONS = [
  { Icon: Building2, label: 'Community centre' },
  { Icon: Landmark, label: 'Council' },
  { Icon: HeartHandshake, label: 'Charity' },
  { Icon: Trees, label: 'Sports club' },
  { Icon: Library, label: 'Library' },
  { Icon: School, label: 'School' },
] as const;

export function Hero() {
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay: number, y = 16) => ({
    initial: { opacity: 0, y: prefersReducedMotion ? 0 : y },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: prefersReducedMotion ? 0.15 : 0.6,
      delay: delay / 1000,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  });

  return (
    <section className="relative overflow-hidden bg-sandron-ink-50 pb-20 pt-24 md:pb-28 md:pt-32 lg:pb-32 lg:pt-36">
      {/* Decorative background gradient */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            'radial-gradient(60% 50% at 100% 0%, rgba(31,107,68,0.08), transparent 70%)',
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
        {/* Eyebrow */}
        <motion.div
          {...fadeUp(0, 12)}
          className="flex items-center gap-3"
        >
          <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
          <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
            Software studio
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          {...fadeUp(90, 24)}
          className="mt-6 max-w-[920px] font-display text-[clamp(40px,6vw,88px)] font-extrabold leading-[1.04] tracking-[-0.025em] text-sandron-ink-900 [text-wrap:balance]"
        >
          We build digital products that bring{' '}
          <span className="bg-gradient-to-br from-spacenest-primary via-[#2E8A5C] to-spacenest-primary bg-[length:200%_200%] bg-clip-text text-transparent motion-safe:animate-gradient-shift">
            communities together
          </span>
          .
        </motion.h1>

        {/* Sub-headline */}
        <motion.p
          {...fadeUp(270, 16)}
          className="mt-7 max-w-[640px] text-[clamp(17px,1.4vw,20px)] leading-[1.6] text-sandron-ink-700 [text-wrap:pretty]"
        >
          Sandron Infotech is an Ireland-based software studio. We design, build,
          and run digital products — including{' '}
          <strong className="font-semibold text-sandron-ink-900">SpaceNest</strong>,
          a booking platform for community centres, schools, and council
          facilities across all 26 counties of Ireland.
        </motion.p>

        {/* CTAs */}
        <motion.div
          {...fadeUp(360, 12)}
          className="mt-10 flex flex-wrap gap-3"
        >
          <Link
            href="/products/spacenest"
            className="group inline-flex h-14 items-center gap-2.5 rounded-full bg-sandron-ink-900 px-7 font-display text-base font-semibold text-white transition-all duration-200 hover:-translate-y-px hover:shadow-[0_12px_32px_-12px_rgba(11,18,32,0.45)] active:translate-y-0 active:scale-[0.98]"
          >
            Explore SpaceNest
            <ArrowRight
              size={18}
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </Link>
          <Link
            href="/contact"
            className="inline-flex h-14 items-center rounded-full border-[1.5px] border-sandron-ink-200 px-7 font-display text-base font-semibold text-sandron-ink-900 transition-all duration-200 hover:border-sandron-ink-900 hover:bg-sandron-ink-50"
          >
            Get in touch
          </Link>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          {...fadeUp(540, 16)}
          className="mt-16 border-t border-sandron-ink-100 pt-8 md:mt-20"
        >
          <p className="text-[13px] font-medium text-sandron-ink-500">
            Built for community organisations across all 26 counties
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4 md:gap-8">
            {TRUST_ICONS.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-sandron-ink-100/60 text-sandron-ink-500 opacity-55 transition-opacity duration-200 hover:opacity-100 md:h-11 md:w-11"
                title={label}
              >
                <Icon size={20} strokeWidth={1.5} />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
